"use client";

import Image from "next/image";
import Link from "next/link";

import { cn } from "@/lib/utils";

interface ProjectCardProps {
  project: {
    slug: string;
    name: string;
    image: string;
  video?: string;
    description: string;
    liveDemo?: string | null;
    sourceCode: string;
    status: string;
    techStack: string[];
    duration?: string;
  };
}

export default function AirbnbProjectCard({ project }: ProjectCardProps) {
  // Default values if not provided
  const duration =
    project.duration ||
    (project.status === "In Progress" ? "In Progress" : "Completed");

  return (
    <div className="group relative w-full max-w-[450px] overflow-hidden rounded-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
      {/* Card container with glass effect and 3D gradient */}
      <div className="relative h-full rounded-xl border border-white/20 bg-gradient-to-br from-white/95 via-white/90 to-white/85 shadow-[0_8px_30px_rgb(0,0,0,0.06)] backdrop-blur-lg hover:shadow-[0_8px_30px_rgb(0,0,0,0.1)] dark:border-zinc-700/30 dark:from-zinc-900/95 dark:via-zinc-900/90 dark:to-zinc-800/85">
        {/* Image section with subtle gradient that blends into content */}
        <div className="relative aspect-[16/9] w-full overflow-hidden">
          <div className="absolute inset-0 z-10 bg-gradient-to-b from-transparent via-transparent to-white/50 dark:to-zinc-900/50" />
          {project.video ? (
            <video
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              src={project.video}
              poster={project.image}
              muted
              playsInline
              autoPlay
              loop
            />
          ) : (
            <Image
              src={project.image}
              alt={project.name}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 450px"
            />
          )}

          {/* Project links */}
          <div className="absolute top-3 right-3 z-20 flex space-x-2">
            {project.sourceCode && (
              <Link
                href={project.sourceCode}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-8 w-8 items-center justify-center rounded-full bg-white/80 text-black backdrop-blur-sm transition-colors hover:bg-white"
                aria-label="Source code"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"></path>
                </svg>
              </Link>
            )}

            {project.liveDemo && (
              <Link
                href={project.liveDemo}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-8 w-8 items-center justify-center rounded-full bg-white/80 text-black backdrop-blur-sm transition-colors hover:bg-white"
                aria-label="Live demo"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
              </Link>
            )}
          </div>
        </div>

        {/* Content section that blends with image */}
        <div className="relative z-20 mt-[-20px] p-5">
          <h3 className="mb-1 text-xl font-bold tracking-tight">
            {project.name}
          </h3>
          <p className="mb-4 line-clamp-2 text-sm text-zinc-600 dark:text-zinc-400">
            {project.description}
          </p>

          {/* Technologies section */}
          <div className="mb-5">
            <h4 className="mb-2 text-sm font-medium text-zinc-500 dark:text-zinc-400">
              Technologies
            </h4>
            <div className="flex items-center gap-3">
              {project.techStack.slice(0, 6).map((tech) => {
                const lower = tech.toLowerCase();
                // Map technology names to the actual PNG filenames stored in /public/projectstack
                const iconMap: Record<string, string> = {
                  react: "1174949_js_react js_logo_react_react native_icon.png",
                  javascript: "icons8-javascript-48.png",
                  js: "icons8-javascript-48.png",
                  node: "icons8-nodejs-50.png",
                  nodejs: "icons8-nodejs-50.png",
                  github: "github(2).png",
                  shadcn: "shadcn-ui.png",
                  "shadcn/ui": "shadcn-ui.png",
                  next: "icons8-next.js-48.png",
                  nextjs: "icons8-next.js-48.png",
                  typescript: "icons8-typescript-50.png",
                  ts: "icons8-typescript-50.png",
                  java: "icons8-java-logo-94.png",
                  "spring boot": "icons8-spring-boot-48.png",
                  springboot: "icons8-spring-boot-48.png",
                  spring: "icons8-spring-boot-48.png",
                  tailwind: "icons8-tailwind-css-48.png",
                  tailwindcss: "icons8-tailwind-css-48.png",
                };
                const fileName =
                  iconMap[lower] ||
                  iconMap[lower.replace(/[^a-z0-9]/g, "")] ||
                  null;
                return (
                  <div key={tech} className="group/tech relative">
                    <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-md bg-zinc-100 dark:bg-zinc-800">
                      {fileName ? (
                        <Image
                          src={`/projectstack/${fileName}`}
                          alt={tech}
                          width={24}
                          height={24}
                          className="h-5 w-5 object-contain"
                        />
                      ) : (
                        <span className="text-[10px] font-semibold text-zinc-500 dark:text-zinc-400">
                          {tech[0].toUpperCase()}
                        </span>
                      )}
                    </div>
                    <span className="absolute -top-8 left-1/2 -translate-x-1/2 rounded bg-black px-2 py-1 text-xs whitespace-nowrap text-white opacity-0 transition-opacity group-hover/tech:opacity-100">
                      {tech}
                    </span>
                  </div>
                );
              })}

              {project.techStack.length > 6 && (
                <div className="flex h-8 w-8 items-center justify-center rounded-md bg-zinc-100 text-xs font-medium text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
                  +{project.techStack.length - 6}
                </div>
              )}
            </div>
          </div>

          {/* Status pill */}
          <div className="mb-5 flex items-center justify-between">
            <div
              className={cn(
                "rounded-full px-2.5 py-1 text-sm",
                project.status === "In Progress"
                  ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-100"
                  : "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-100"
              )}
            >
              {duration}
            </div>
          </div>

          {/* CTA Button */}
          <Link
            href={`/projects/${project.slug}`}
            className={cn(
              "flex w-full items-center justify-center",
              "rounded-lg bg-zinc-900 px-4 py-2.5 text-center font-medium text-white",
              "transition-colors duration-300 hover:bg-black",
              "dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-200"
            )}
          >
            View Project
          </Link>
        </div>
      </div>
    </div>
  );
}
